import type { IncomingMessage } from 'http'
import { makeAutoObservable } from 'mobx'

import type { CacheState } from './CacheStore'
import { CacheStore } from './CacheStore'
import type { DeviceStoreState } from './DeviceStore'
import { DeviceStore } from './DeviceStore'
import type { IsomorphicStore } from './IsomorphicStore'
import { LinkingStore } from './linking/LinkingStore'
import { LocalFeatureOverridesStorage } from './LocalFeatureOverrideStore'
import { SdkClientStore } from './SdkClientStore'
import { isBrowser, isTests } from '@shared/util/env'

export class RootStore implements IsomorphicStore<RootStoreState> {
    isomorphicKeys = ['deviceStore', 'cacheStore'] as const
    linkingStore: LinkingStore

    private constructor(
        initialState: RootStoreState,
        readonly sdk: SdkClientStore,
        readonly deviceStore: DeviceStore,
        readonly cacheStore: CacheStore,
        readonly featureOverrideStore: LocalFeatureOverridesStorage,
    ) {
        this.linkingStore = new LinkingStore(deviceStore)
        if (initialState) this.restore(initialState)
    }

    static fromState(state: RootStoreState) {
        const store = new RootStore(
            state,
            SdkClientStore.create(),
            DeviceStore.create(),
            CacheStore.create(),
            LocalFeatureOverridesStorage.create(),
        )

        return makeAutoObservable(store)
    }

    static fromRequest(request?: IncomingMessage) {
        if (!request && !isBrowser() && !isTests()) {
            throw new Error(
                'To create RootStore during SSR, you have to pass in the request object',
            )
        }

        const store = new RootStore(
            {},
            SdkClientStore.create(request),
            DeviceStore.create(request),
            CacheStore.create(),
            LocalFeatureOverridesStorage.create(),
        )

        return makeAutoObservable(store)
    }

    async load() {
        try {
            await this.featureOverrideStore.load()
        } catch (error) {
            // Silently handle feature override loading errors to prevent unhandled promise rejections
            // This can happen when crypto.randomUUID() is unavailable or localStorage access fails
            console.error('Failed to load feature overrides:', error)
        }
    }

    restore(initialState: RootStoreState) {
        const keys = this.isomorphicKeys

        for (const key of keys) {
            const state = initialState[key]
            if (!state) continue
            this[key].restore(state as any)
        }
    }

    toJSON() {
        const keys = this.isomorphicKeys
        const result = {}

        for (const key of keys) {
            const state = (this as any)[key].toJSON()
            if (!state) continue
            ;(result as any)[key] = state
        }

        return result
    }
}

export interface RootStoreState {
    deviceStore?: DeviceStoreState
    cacheStore?: CacheState
}
